import { SmbAttachmentStatus, type Transaction } from "@/apis/gql/types";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TypographySmall } from "@/components/ui/typography";
import { cn } from "@/lib/utils";
import { useUpdateBankTransaction } from "@/queries/transactions/update-bank-transaction";
import { Check, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";

type Props = {
  transaction: Transaction;
  onMarkAsReviewed: () => Promise<void>;
};

export const NoInvoiceRequiredCell = ({
  transaction,
  onMarkAsReviewed,
}: Props) => {
  const { t } = useTranslation();

  const [updateBankTransaction] = useUpdateBankTransaction();

  const handleRemoveSuggestion = () => {
    updateBankTransaction({
      bankTransactionIds: [transaction.bankTxnId],
      updatedFields: {
        smbAttachmentStatus: SmbAttachmentStatus.NOT_ATTACHED,
      },
    });
  };

  const handleMarkAsReviewed = () => {
    onMarkAsReviewed();
  };

  return (
    <div className="group flex items-center justify-center w-full px-2">
      <div
        className={cn(
          "flex items-center justify-center w-full border rounded-lg cursor-pointer p-2 shadow-sm",
        )}
      >
        <div className="flex items-center justify-center mr-2">
          <Sparkles className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex flex-1 flex-col py-2">
          <TypographySmall className="text-sm">
            {t("transactions.suggestions.no_invoice_required")}
          </TypographySmall>
        </div>

        <div className="flex flex-row gap-1 ml-2">
          <div className="flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSuggestion();
                    }}
                    tabIndex={-1}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" align="center">
                  {t("transactions.suggestions.remove_attachment")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsReviewed();
                    }}
                    tabIndex={-1}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" align="center">
                  {t("transactions.suggestions.remove_attachment")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </div>
  );
};
